import type { StringIndexedObject } from "@kanda-libs/ks-component-ts";
import { useFuse, type FuseHit } from "@kanda-libs/ks-design-library";
import { useEffect } from "react";
import { QueryType } from "store/slices/query";
import { useDebounce } from "use-debounce";
import useQuery from "../useQuery";
import { DEBOUNCE } from "./constants";

export interface SearchQueryHook<T> {
  hits: FuseHit<T>[];
  query: string;
  setQuery: (query: string) => void;
}

export default function useSearchQuery<T>(
  data: T[],
  queryType: QueryType | "",
  searchOptions: StringIndexedObject
): SearchQueryHook<T> {
  const [query, setQuery] = useQuery(queryType);
  const { hits, setQuery: fuseSetQuery } = useFuse(data, searchOptions, "");

  const [debouncedQuery] = useDebounce(query, DEBOUNCE);

  /**
   * Update query when debouncedQuery is updated
   */
  useEffect(() => {
    setQuery(debouncedQuery || "");
    fuseSetQuery(debouncedQuery || "");
  }, [debouncedQuery]);

  return {
    hits,
    query: debouncedQuery,
    setQuery,
  };
}
